/**
 * Central API service to handle all API calls with proper error handling and CORS support
 * This service serves as a foundation for all other services in the application
 */
import axios from 'axios';
import { getBaseApiUrl, getCorsSettings } from '../utils/apiConfig';
import { toast } from 'react-toastify';

const API_BASE_URL = getBaseApiUrl();
const IS_DEV = import.meta.env.DEV;
const corsSettings = getCorsSettings();

// Environment-based configuration
//const isDevelopment = process.env.NODE_ENV === 'development' || !process.env.NODE_ENV;
const isDevelopment = IS_DEV;

// API path constants
const AUTH_PATH = '/auth';

// Base API URLs for different environments
export const API_CONFIG = {
  // Base URL for API calls - automatically determined based on environment
  baseUrl: API_BASE_URL,
  
  // Default timeout in milliseconds
  timeout: 15000,
  
  // Whether to include credentials (cookies, auth headers) with requests
  ...corsSettings,
  
  // Default headers for all requests
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
};

// Global token refresh state shared across all axios clients
// This prevents multiple clients from triggering parallel refresh requests
let isRefreshing = false;
let failedQueue = [];

// Module-level flag so the "wrong environment" redirect fires only once.
// A blocked tab can have several in-flight requests failing with the same
// 403 at once (e.g. polling + a user action), and every one of them hits
// this interceptor - without this guard each would try to sign out and
// redirect again. Mirrors isAuthRedirectInProgress in AuthContext.jsx.
let isEnvironmentRedirectInProgress = false;

const processQueue = (error, token = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Creates and configures an axios instance for API requests
 * @returns {import('axios').AxiosInstance} Configured axios instance
 */
export const createApiClient = (customConfig = {}) => {
  // Merge default config with any custom config
  const config = {
    ...API_CONFIG,
    ...customConfig,
    headers: {
      ...API_CONFIG.headers,
      ...(customConfig.headers || {}),
    }
  };

  // Create axios instance
  const client = axios.create({
    baseURL: config.baseUrl,
    timeout: config.timeout,
    withCredentials: config.withCredentials,
    headers: config.headers,
  });

  // Add request interceptor for authentication
  client.interceptors.request.use(
    (config) => {
      // Get authentication token from memory
      const token = localStorage.getItem('auth_token');

      // If token exists, add to headers
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // Add response interceptor for error handling
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      
      // Log detailed error information
      if (isDevelopment) {
        if (error.response) {
          console.error('API Error Response:', {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          });
        } else if (error.request) {
          console.error('API No Response:', error.request);
        } else {
          console.error('API Error:', error.message);
        }
      }
      
      // Handle the environment access gate: a dev-origin account hitting
      // production, or a production-origin account hitting dev. The token is
      // otherwise still valid - only this per-route check failed - so unlike
      // the 401 handling below there is nothing to refresh. We sign the
      // session out and send the user to a dedicated page carrying the
      // backend's already direction-aware message.
      if (
        error.response &&
        error.response.status === 403 &&
        error.response.data?.error === 'ACCESS_DENIED_ENVIRONMENT'
      ) {
        if (
          !isEnvironmentRedirectInProgress &&
          !window.location.pathname.startsWith('/wrong-environment')
        ) {
          isEnvironmentRedirectInProgress = true;

          // Clear token and identity so an open tab stops polling as this user
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_id');

          // No returnUrl - the account cannot use this site at all, so
          // sending the user back to where they were would be wrong.
          const message = error.response.data?.message || '';
          window.location.replace(`/wrong-environment?message=${encodeURIComponent(message)}`);
        }

        return Promise.reject(error);
      }

      // Handle authentication errors
      if (error.response && error.response.status === 401) {
        // Don't retry if this is already a refresh request to avoid infinite loops
        if (originalRequest.url.includes('/auth/refresh')) {
          // Clear token from memory
          localStorage.removeItem('auth_token');

          // Dispatch logout event for auth context to handle
          if (!window.location.pathname.startsWith('/auth/') && !window.location.pathname.startsWith('/login')) {
            window.dispatchEvent(new CustomEvent('auth:logout', { detail: 'Session expired' }));
          }

          return Promise.reject(error);
        }

        // If we're already refreshing, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return client(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        // Set refreshing flag
        isRefreshing = true;

        // Try to refresh token using tokenManager (which handles cookies)
        try {
          // Import authService dynamically to avoid circular dependencies
          const authServiceModule = await import('./authService');
          await authServiceModule.refreshAccessToken();

          // If we got a new token (stored in tokenManager by authService.refreshAccessToken)
          const newToken = localStorage.getItem('auth_token');
          if (newToken) {
            // Update authorization header
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

            // Process pending requests
            processQueue(null, newToken);

            // Retry original request
            return client(originalRequest);
          }
        } catch {
          console.log('Token refresh not implemented or failed, reloading page...');
        } finally {
          // Reset refreshing flag
          isRefreshing = false;
        }

        // ===== Token refresh failed or not implemented - reload page =====

        // Clear token
        localStorage.removeItem('auth_token');

        // Save current URL for return after login (if not already on login/auth pages)
        const currentPath = window.location.pathname + window.location.search;
        if (!currentPath.startsWith('/login') && !currentPath.startsWith('/auth/')) {
          localStorage.setItem('returnUrl', currentPath);
          console.log('💾 Saved return URL:', currentPath);
        }

        // Show notification to user
        toast.warning('Session expired, reloading...', {
          position: 'top-center',
          autoClose: 1000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false
        });

        // Reload page after showing notification
        setTimeout(() => {
          console.log('🔄 Reloading page due to expired session...');
          window.location.reload();
        }, 1000);

        return Promise.reject(error);
      }
      
      return Promise.reject(error);
    }
  );
  
  return client;
};

// Default API client
const apiClient = createApiClient();

/**
 * Standardized API error class
 * Provides consistent error handling across the application
 */
export class ApiError extends Error {
  constructor(message, status, data, originalError = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
    this.originalError = originalError;
    this.timestamp = new Date();
    
    // Get friendly message based on status code
    this.friendlyMessage = this.getFriendlyMessage();
  }
  
  // Get user-friendly error message based on status code
  getFriendlyMessage() {
    switch (this.status) {
      case 400:
        return 'The request was invalid. Please check your input and try again.';
      case 401:
        return 'You need to log in to access this resource.';
      case 403:
        return 'You do not have permission to access this resource.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'There was a conflict with the current state of the resource.';
      case 422:
        return 'The request was well-formed but contained invalid data.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'An internal server error occurred. Please try again later.';
      case 503:
        return 'The service is temporarily unavailable. Please try again later.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
  
  // Get field-specific validation errors for forms
  getFieldErrors() {
    if (this.status === 400 || this.status === 422) {
      if (this.data && this.data.errors) {
        return this.data.errors;
      }
    }
    return {};
  }
}

/**
 * Generic API request helper with enhanced error handling
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {string} url - API endpoint URL (without base)
 * @param {Object} data - Request data (for POST, PUT)
 * @param {Object} config - Additional axios config
 * @returns {Promise<any>} - Response data
 * @throws {ApiError} - Standardized API error
 */
export const apiRequest = async (method, url, data = null, config = {}) => {
  try {
    if (isDevelopment) {
      console.log(`API Request: ${method} ${url}`, method !== 'GET' ? data : '');
    }
    
    const response = await apiClient({
      method,
      url,
      data: method !== 'GET' ? data : undefined,
      params: method === 'GET' ? data : undefined,
      ...config,
    });
    
    return response.data;
  } catch (error) {
    // Extract error details
    const status = error.response?.status || 0;
    const responseData = error.response?.data || {};
    const message =
      (typeof responseData.error === 'string' && responseData.error) ||
      (typeof responseData.message === 'string' && responseData.message) ||
      error.message ||
      'Unknown error';
    
    // Log error in development
    if (isDevelopment) {
      console.error(`API Error in ${method} ${url}:`, {
        status,
        message,
        data: responseData,
        originalError: error
      });
    }
    
    // Track errors in production (could be integrated with monitoring service)
    if (!isDevelopment && status >= 500) {
      // Example: Send to error tracking service
      // errorTrackingService.capture({
      //   type: 'api_error',
      //   method,
      //   url,
      //   status,
      //   message
      // });
    }
    
    // Transform to standardized ApiError and throw
    throw new ApiError(
      message,
      status,
      responseData,
      error
    );
  }
};

// Convenience methods for common HTTP methods
export const get = (url, params, config) => apiRequest('GET', url, params, config);
export const post = (url, data, config) => apiRequest('POST', url, data, config);
export const put = (url, data, config) => apiRequest('PUT', url, data, config);
export const patch = (url, data, config) => apiRequest('PATCH', url, data, config);
export const del = (url, config) => apiRequest('DELETE', url, null, config);

export default {
  client: apiClient,
  get,
  post,
  put,
  patch,
  del,
  createApiClient,
};
